import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  ShieldCheck, Search, RefreshCw, ChevronRight, Clock, Filter, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

// Nice labels + colour swatches per action type
const ACTION_META = {
  test_data_purge: { label: 'Test data purge', color: 'bg-[#FF3B6B]/10 text-[#FF3B6B]' },
  booking_cancel: { label: 'Booking cancelled', color: 'bg-[#FF9F45]/10 text-[#C26A14]' },
  refund_created: { label: 'Refund issued', color: 'bg-[#8B5CF6]/10 text-[#6D3FC9]' },
  employee_create: { label: 'Employee created', color: 'bg-[#06D6A0]/10 text-[#058B69]' },
  employee_delete: { label: 'Employee deleted', color: 'bg-[#FF3B6B]/10 text-[#FF3B6B]' },
  employee_pin_reset: { label: 'PIN reset', color: 'bg-[#FFD166]/20 text-[#9C6B12]' },
  employee_pin_clear: { label: 'PIN cleared', color: 'bg-[#FFD166]/20 text-[#9C6B12]' },
  employee_password_reset: { label: 'Password reset', color: 'bg-[#FFD166]/20 text-[#9C6B12]' },
  employee_unlock: { label: 'Employee unlocked', color: 'bg-[#06D6A0]/10 text-[#058B69]' },
};

const actionPill = (action) => {
  const meta = ACTION_META[action] || { label: action, color: 'bg-[#E8E5DF] text-[#1E1E24]' };
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`} data-testid={`audit-action-${action}`}>
      {meta.label}
    </span>
  );
};

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export const CrmAudit = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ actions: [], target_types: [], admins: [] });
  const [filters, setFilters] = useState({
    action: 'all',
    admin_user_id: 'all',
    target_type: 'all',
    date_from: '',
    date_to: '',
    search: '',
  });
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchMeta = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/audit-logs/meta`, { withCredentials: true });
      setMeta(res.data);
    } catch (e) {
      // non-fatal
    }
  };

  const fetchLogs = async (resetPage = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action !== 'all') params.set('action', filters.action);
      if (filters.admin_user_id !== 'all') params.set('admin_user_id', filters.admin_user_id);
      if (filters.target_type !== 'all') params.set('target_type', filters.target_type);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.search) params.set('search', filters.search);
      const skip = resetPage ? 0 : page * PAGE_SIZE;
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));

      const res = await axios.get(`${API_URL}/api/admin/audit-logs?${params.toString()}`, { withCredentials: true });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      if (resetPage) setPage(0);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    fetchLogs(true);
    /* eslint-disable-next-line */
  }, [filters.action, filters.admin_user_id, filters.target_type, filters.date_from, filters.date_to, filters.search]);

  const applySearch = () => setFilters((f) => ({ ...f, search: searchInput.trim() }));
  const resetFilters = () => {
    setSearchInput('');
    setFilters({ action: 'all', admin_user_id: 'all', target_type: 'all', date_from: '', date_to: '', search: '' });
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const hasActiveFilters = filters.action !== 'all' || filters.admin_user_id !== 'all' ||
    filters.target_type !== 'all' || filters.date_from || filters.date_to || filters.search;

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-6 h-6 text-[#FF3B6B]" />
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Audit Log
            </h1>
          </div>
          <p className="text-[#6B6B7A] text-sm">
            Every sensitive admin action, timestamped and attributable. {total > 0 && <span className="font-medium">{total} records</span>}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchLogs()}
          className="rounded-full border-[#E8E5DF]"
          data-testid="audit-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-[#6B6B7A] text-sm">
          <Filter className="w-4 h-4" /> Filters
          {hasActiveFilters && (
            <button onClick={resetFilters} className="ml-auto text-xs flex items-center gap-1 text-[#FF3B6B] hover:underline" data-testid="audit-clear-filters">
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <Select value={filters.action} onValueChange={(v) => setFilters((f) => ({ ...f, action: v }))}>
            <SelectTrigger data-testid="audit-filter-action" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {meta.actions.map((a) => (
                <SelectItem key={a} value={a}>{ACTION_META[a]?.label || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.admin_user_id} onValueChange={(v) => setFilters((f) => ({ ...f, admin_user_id: v }))}>
            <SelectTrigger data-testid="audit-filter-admin" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Admin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All admins</SelectItem>
              {meta.admins.map((a) => (
                <SelectItem key={a.admin_user_id} value={a.admin_user_id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.target_type} onValueChange={(v) => setFilters((f) => ({ ...f, target_type: v }))}>
            <SelectTrigger data-testid="audit-filter-target" className="rounded-xl border-[#E8E5DF]">
              <SelectValue placeholder="Target type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All targets</SelectItem>
              {meta.target_types.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            className="rounded-xl border-[#E8E5DF]"
            data-testid="audit-filter-from"
          />
          <Input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            className="rounded-xl border-[#E8E5DF]"
            data-testid="audit-filter-to"
          />

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
              <Input
                placeholder="Search summary…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
                className="pl-9 rounded-xl border-[#E8E5DF]"
                data-testid="audit-filter-search"
              />
            </div>
            <Button onClick={applySearch} className="rounded-xl bg-[#1E1E24] hover:bg-[#2E2E34] text-white" data-testid="audit-search-apply">Go</Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">When</TableHead>
              <TableHead className="w-44">Action</TableHead>
              <TableHead className="w-40">Admin</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead className="w-32">Target</TableHead>
              <TableHead className="w-14"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#6B6B7A]">Loading…</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#6B6B7A]" data-testid="audit-empty">
                No audit records match these filters.
              </TableCell></TableRow>
            ) : entries.map((e) => (
              <TableRow
                key={e.audit_id}
                onClick={() => setSelected(e)}
                className="cursor-pointer hover:bg-[#FFFDF8]"
                data-testid={`audit-row-${e.audit_id}`}
              >
                <TableCell className="text-xs text-[#6B6B7A] whitespace-nowrap">
                  <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatWhen(e.created_at)}</div>
                </TableCell>
                <TableCell>{actionPill(e.action)}</TableCell>
                <TableCell className="text-sm text-[#1E1E24]">{e.admin_name || '—'}</TableCell>
                <TableCell className="text-sm text-[#1E1E24]">
                  {e.summary || <span className="text-[#6B6B7A] italic">No summary</span>}
                </TableCell>
                <TableCell className="text-xs text-[#6B6B7A] font-mono truncate max-w-[150px]">
                  {e.target_id || '—'}
                </TableCell>
                <TableCell>
                  <ChevronRight className="w-4 h-4 text-[#6B6B7A]" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-[#6B6B7A]">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm" className="rounded-full"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="audit-prev-page"
            >Previous</Button>
            <Button
              variant="outline" size="sm" className="rounded-full"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="audit-next-page"
            >Next</Button>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto" data-testid="audit-detail-drawer">
          <SheetHeader>
            <SheetTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Audit entry</SheetTitle>
            <SheetDescription>
              {selected?.audit_id && <span className="font-mono text-xs">{selected.audit_id}</span>}
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-4 text-sm">
              <div>{actionPill(selected.action)}</div>
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <span className="text-[#6B6B7A]">When</span>
                <span className="text-[#1E1E24]">{formatWhen(selected.created_at)}</span>
                <span className="text-[#6B6B7A]">Admin</span>
                <span className="text-[#1E1E24]">{selected.admin_name} <span className="text-xs text-[#6B6B7A]">({selected.admin_role})</span></span>
                <span className="text-[#6B6B7A]">Admin ID</span>
                <span className="text-[#1E1E24] font-mono text-xs break-all">{selected.admin_user_id || '—'}</span>
                <span className="text-[#6B6B7A]">Target</span>
                <span className="text-[#1E1E24] font-mono text-xs break-all">
                  {selected.target_type ? `${selected.target_type} · ` : ''}{selected.target_id || '—'}
                </span>
                <span className="text-[#6B6B7A]">IP</span>
                <span className="text-[#1E1E24] font-mono text-xs">{selected.ip || '—'}</span>
              </div>
              <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
                <p className="text-[#1E1E24]">{selected.summary || <span className="italic text-[#6B6B7A]">No summary</span>}</p>
              </div>
              {(selected.extra || selected.counts || selected.preview || selected.mode) && (
                <div>
                  <p className="text-xs font-semibold text-[#6B6B7A] uppercase tracking-wide mb-2">Details</p>
                  <pre className="bg-[#1E1E24] text-[#F7F5F0] rounded-xl p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words" data-testid="audit-detail-json">
{JSON.stringify({
  ...(selected.extra || {}),
  ...(selected.counts ? { counts: selected.counts } : {}),
  ...(selected.preview ? { preview: selected.preview } : {}),
  ...(selected.mode ? { mode: selected.mode } : {}),
}, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CrmAudit;
